import { useState, useCallback } from 'react';
import { ApiError } from '../types/api';
import {
  IncomeStatisticsResponse,
  IncomeBreakdownResponse,
  ProductStatisticsItem,
  ServiceStatisticsItem,
  PaymentMethodStatisticsItem,
} from '../types/statistics';
import { getAuthenticatedFetch } from '../utils/api';
import { useErrorHandler } from '../utils/errorHandler';

interface StatisticsQueryParams {
  startDate: string; // ISO date string
  endDate: string; // ISO date string
}

export const useStatisticsApi = () => {
  const [error, setError] = useState<ApiError | null>(null);
  const { handleApiError: handleError } = useErrorHandler();

  const handleApiError = (err: any): ApiError => {
    const apiError = handleError(err);
    setError(apiError);
    return apiError;
  };

  const clearError = () => setError(null);

  const fetchIncomeStatistics = useCallback(async (
    queryParams: StatisticsQueryParams,
  ): Promise<IncomeStatisticsResponse> => {
    try {
      clearError();
      const queryString = new URLSearchParams({
        startDate: queryParams.startDate,
        endDate: queryParams.endDate,
      }).toString();

      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/statistics/income?${queryString}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: IncomeStatisticsResponse = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  }, []);

  const fetchIncomeStatisticsBreakdown = useCallback(async (
    queryParams: StatisticsQueryParams,
  ): Promise<IncomeBreakdownResponse> => {
    try {
      clearError();
      const queryString = new URLSearchParams({
        startDate: queryParams.startDate,
        endDate: queryParams.endDate,
      }).toString();

      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/statistics/income/breakdown?${queryString}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: IncomeBreakdownResponse = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  }, []);

  const fetchProductsStatistics = async (
    queryParams: StatisticsQueryParams,
  ): Promise<ProductStatisticsItem[]> => {
    try {
      clearError();
      const queryString = new URLSearchParams({
        startDate: queryParams.startDate,
        endDate: queryParams.endDate,
      }).toString();

      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/statistics/products?${queryString}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ProductStatisticsItem[] = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const fetchServicesStatistics = async (
    queryParams: StatisticsQueryParams,
  ): Promise<ServiceStatisticsItem[]> => {
    try {
      clearError();
      const queryString = new URLSearchParams({
        startDate: queryParams.startDate,
        endDate: queryParams.endDate,
      }).toString();

      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/statistics/services?${queryString}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ServiceStatisticsItem[] = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const fetchPaymentMethodsStatistics = async (
    queryParams: StatisticsQueryParams,
  ): Promise<PaymentMethodStatisticsItem[]> => {
    try {
      clearError();
      const queryString = new URLSearchParams({
        startDate: queryParams.startDate,
        endDate: queryParams.endDate,
      }).toString();

      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/statistics/payment-methods?${queryString}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: PaymentMethodStatisticsItem[] = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  return {
    error,
    clearError,
    fetchIncomeStatistics,
    fetchIncomeStatisticsBreakdown,
    fetchProductsStatistics,
    fetchServicesStatistics,
    fetchPaymentMethodsStatistics,
  };
};

